<template>
	<div>
		<ClientOnly>
			<div v-if="isBlocking || isLoading" class="horizontal-title-list-blocking">
				<div class="blocking-element-container" v-for="tmp in [1, 2, 3]" :key="tmp">
					<div class="blocking-element blocking-title-poster"></div>
				</div>
			</div>
		</ClientOnly>

		<div v-show="!(isBlocking || isLoading)" class="horizontal-title-list">
			<div class="blocking-element"></div>

			<component
				:is="titlesContainerComp.cmp"
				v-bind="titlesContainerComp.props"
				v-on="titlesContainerComp.methods"
				ref="horizontal-scrollbar"
			>
				<template #title>
					<h3 v-if="showMobileTitle && isMobileView" class="title-text section-title">{{ titleText }}</h3>
				</template>

				<div
					class="horizontal-title-list__item slider-item"
					:class="{
						'horizontal-title-list__item--with-position': showPosition,
						'swiper-slide': isMobileView,
					}"
					v-for="(title, index) in titles"
					:key="`${title.id}-${title.objectType}`"
				>
					<div
						v-if="showPosition && !isMobileView"
						class="horizontal-title-list__item-position"
						:style="{ color: '#222c38' }"
					>
						{{ index + 1 }}
					</div>

					<TitleDetailLink :fullPath="title.content.fullPath" :title="title">
						<div class="title-detail-link-container">
							<div class="poster-container">
								<PosterGraphql
									:title="title"
									:watchNowOffer="title.watchNowOffer"
									:showRibbon="!isLoggedIn"
									:showQuickActions="showQuickActions && isLoggedIn && isMounted"
									:hasMoreLikeThis="hasMoreLikeThis"
									:lazy="index >= 0"
									:showAvailabilityBadge="true"
									availabilityBadgeSize="short"
									:additionalContexts="additionalContexts"
									@click="handleTitleClick(index, title)"
								/>

								<div
									v-if="showPosition && isMobileView"
									class="horizontal-title-list__item-position"
									:style="{ color: '#222c38' }"
								>
									{{ index + 1 }}
								</div>

								<WatchNowButton
									v-if="title.watchNowOffer && !isMobileView"
									:class="[
										'watch-now-button__desktop',
										{ 'watch-now-button__desktop--atp': isAppleTvPlus },
									]"
									:watchNowOffer="title.watchNowOffer"
									:titleObjectId="title.objectId"
									:titleObjectType="title.objectType"
									:title="title"
									:inQuickActions="true"
									:isExpanded="false"
									color="secondary"
									size="large"
									:overwriteClickTracking="true"
									@watchNowClicked="handleWatchNowClick(title)"
								>
									<template #provider-icon v-if="isAppleTvPlus">
										<img :src="appleTvPlusIcon" height="16" />
									</template>
									<span class="label" :class="[{ 'pt-4': isAppleTvPlus }]">
										{{ isFrench ? $t('WEBAPP_SEENLIST_WATCHNOW') : $t('WEBAPP_WATCHNOW') }}
									</span>
								</WatchNowButton>
							</div>
							<div v-if="isMobileView" class="title-mob" @click="handleTitleClick(index, title)">
								<span>{{ getTitleName(title) }}</span>
							</div>
						</div>
					</TitleDetailLink>

					<SingleRankingDisplay
						class="title-info"
						v-if="isMobileView && title.streamingCharts.edges?.[0].streamingChartInfo"
						:hideRank="true"
						:rank="title.streamingCharts.edges?.[0].streamingChartInfo?.rank"
						:trend="title.streamingCharts.edges?.[0].streamingChartInfo?.trend"
						:trendDifference="title.streamingCharts?.edges?.[0].streamingChartInfo?.trendDifference"
						:runtime="title.content.runtime"
						:releaseYear="title.content.originalReleaseYear"
					/>

					<WatchNowButton
						v-if="title.watchNowOffer && isMobileView"
						class="watch-now-button__mobile"
						:watchNowOffer="title.watchNowOffer"
						:titleObjectId="title.objectId"
						:titleObjectType="title.objectType"
						:title="title"
						:inQuickActions="true"
						:isExpanded="false"
						:isClickoutAnchor="true"
						color="primary"
						size="large"
						:overwriteClickTracking="true"
						@watchNowClicked="handleWatchNowClick(title)"
					>
						{{ $t('WEBAPP_WATCHNOW') }}
					</WatchNowButton>
				</div>
				<!-- possible to add content after -->
				<slot name="after" />
			</component>
		</div>
	</div>
</template>

<script setup lang="ts">
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import {
	SnowplowTitleContextGraphql,
	SnowplowClickoutContextGraphql,
	type SnowplowContext,
} from '@/helpers/tracking/providers/snowplow-contexts';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import SingleRankingDisplay from '@/components/streaming-charts/SingleRankingDisplay.vue';
import WatchNowButton from '@/components/buttons/WatchNowButton.vue';
import Top10TitleSwiper from './Top10TitleSwiper.vue';
import { getSelectedTab } from './helper';
import { computed, ref, watch } from 'vue';
import { useMounted } from '@vueuse/core';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useMediaQuery } from '@vueuse/core';
import Provider from '@/interfaces/provider';

const props = withDefaults(
	defineProps<{
		provider: Provider;
		titles?: TitleDetail[];
		isBlocking?: boolean;
		preventDefault?: boolean;
		showQuickActions?: boolean;
		hasMoreLikeThis?: boolean;
		showSeenList?: boolean;
		showMobileTitle?: boolean;
		titleText?: string;
		showPosition?: boolean;
		additionalContexts?: SnowplowContext[];
	}>(),
	{
		titles: () => [],
		titleText: '',
		additionalContexts: () => [],
	}
);

const emit = defineEmits<{
	(e: 'scrollbarClicked', payload: { direction: 'left' | 'right'; position: number }): void;
	(e: 'onTitleClick', payload: { index: number; title: TitleDetail }): void;
}>();

const { isLoggedIn } = storeToRefs(useUserStore());
const languageStore = storeToRefs(useLanguageStore());
const routingStore = useRoutingStore();

const isMounted = useMounted();
const isLoading = ref(false);

const isMobileViewport = useMediaQuery('(max-width: 512px)');
const isMobileView = computed(() => isMounted.value && isMobileViewport.value);

const isFrench = computed(() => languageStore.language.value === 'fr');

// For Apple TV Plus we override the watch now button position and icon
const isAppleTvPlus = computed(() => props.provider.shortName === 'atp');
const appleTvPlusIcon = `/${ASSETS_DIR}/img/icons/ATP_white_logo_sm.svg`;

const titlesContainerComp = computed(() => {
	if (isMobileView.value) {
		return {
			cmp: Top10TitleSwiper,
			props: {
				titleCount: props.titles.length,
			},
			methods: { slideChange },
		};
	}

	return {
		cmp: HiddenHorizontalScrollbar,
		props: {
			additionalContexts: props.additionalContexts,
		},
	};
});

watch(
	() => props.titles,
	() => {
		isLoading.value = true;

		setTimeout(() => {
			isLoading.value = false;
		}, 200);
	}
);

function slideChange(payload: { direction: 'left' | 'right'; position: number }) {
	emit('scrollbarClicked', payload);
}

function handleTitleClick(index: number, title: TitleDetail) {
	emit('onTitleClick', { index, title });
}

function getTitleName(title: TitleDetail) {
	if (title.__typename === 'Season') {
		return title?.show?.content?.title;
	}

	return title.content.title;
}

function handleWatchNowClick(titleNode: TitleDetail) {
	const selectedTab = getSelectedTab(routingStore.activeRoute?.name as string);

	const contexts: SnowplowContext[] = [new SnowplowTitleContextGraphql(titleNode.objectId, titleNode.objectType)];
	if (titleNode.watchNowOffer) {
		contexts.push(SnowplowClickoutContextGraphql.fromProviderOffer(titleNode.watchNowOffer));
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'watchnow_clicked',
			label: 'top-10-titles',
			property: selectedTab,
		},
		contexts
	);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';
@import '@/styles/sports.scss';
$experiment_primary_color: $jw-color-gold;

.horizontal-title-list-blocking {
	display: flex;
	margin-top: 10px;

	@media (scripting: enabled) and (max-width: 512px) {
		min-height: 520px;
	}

	.blocking-element-container {
		display: inline-block;
		margin-right: 10px;
		border-radius: 5px;
		@include title-poster-width();

		.blocking-title-poster {
			@include title-poster-height();
		}
	}
}
.swiper-container {
	.title-text {
		text-align: center;
		font-size: 22px;
		color: #fff;
		padding: 24px 16px;
		margin: 0;
	}
	.title-info {
		margin-top: -8px;

		:deep(.title-ranking-list__rank) {
			font-size: 16px;
		}
	}
	.horizontal-title-list__item-position {
		height: fit-content !important;
		font-size: 88px !important;
		display: flex;
		align-items: flex-end;
		margin-inline-end: -30px !important;
		letter-spacing: unset !important;
		z-index: 2;
		position: absolute;
		bottom: 0;
		height: fit-content;
		left: -19px;
	}
	.swiper-slide {
		background: #0c131e;
		padding: 0 0 40px 0 !important;
		display: flex;
		justify-content: center;
		flex-direction: column;
		align-items: center;
		width: calc(100vw - 48px);
		border-radius: 8px;
		margin: 0 !important;
	}
	.title-detail-link-container {
		display: flex;
		flex-direction: column;
		align-items: center;
	}
	.poster-container {
		position: relative;
		width: fit-content;
	}
	.title-mob {
		font-weight: 900;
		font-size: 16px;
		margin-top: 24px;
		color: #fff;
		text-align: center;
		margin-bottom: 8px;
	}
}
.horizontal-title-list {
	$parent: &;

	@media (scripting: enabled) and (max-width: 512px) {
		min-height: 520px;
	}

	--margin-top: 10px;
	margin-top: var(--margin-top);

	&__item--with-position {
		padding-bottom: 32px;
		margin-bottom: -32px;
		display: flex;
		align-items: flex-end;

		#{$parent}__item-position {
			height: 100%;
			line-height: 100%;
			font-size: 180px;
			letter-spacing: -20px;
			margin-inline-end: -2px;
			font-weight: 900;
			margin-inline-start: -4px;

			@media (min-width: 512px) {
				&:dir(rtl) {
					margin-inline-end: -12px;
					margin-inline-start: 24px;
				}
			}
		}
	}

	&__item {
		margin: 0 5px 0;
		position: relative;

		z-index: 10;

		&__caption {
			margin: 5px;
			overflow: hidden;
			max-width: 155px;
			white-space: nowrap;
			text-overflow: ellipsis;
		}

		&:first-child {
			margin-inline-start: 0;
		}
	}

	.title-poster {
		@include title-poster-width();
	}

	.timeline & {
		margin-inline-start: -35px;

		.hidden-horizontal-scrollbar__items {
			padding-left: 35px;
			padding-right: 35px;

			/* full width style for mobile viewports */
			@media (max-width: $screen-xs-max) {
				padding-inline-start: 50px;
			}
		}
	}
}

.pointer-none {
	pointer-events: none;
}
.watch-now-button {
	&__desktop {
		position: absolute;
		bottom: 10px;
		width: 170px;
		margin-inline-start: 10px;
		.label {
			text-overflow: ellipsis;
			overflow: hidden;
			&.pt-4 {
				padding-top: 4px;
			}
		}
		&--atp {
			position: relative;
			margin-top: 12px;
			margin-inline-start: 0;
			width: 100%;
			bottom: auto;
			:deep(.basic-button__content) {
				gap: 4px;
			}
		}
	}
}
</style>
